import { Filter } from '@vrplatform/components';
import type { SelectItem } from '@vrplatform/components';
import { day, hasValue } from '@vrplatform/utils';
import { Box, Group } from '@mantine/core';
import type { activeStatus_enum } from '@vrplatform/graphql';
import { type ComponentProps, type ReactNode, useMemo } from 'react';
import { CustomFilter } from '../../../../components';
import { JournalEntryAccountFilter } from './JournalEntryAccountFilter';
import { JournalEntrySubTypeFilter } from './JournalEntrySubTypeFilter';
import { JournalEntryTypeFilter } from './JournalEntryTypeFilter';
import { useJournalEntriesTableFilter } from '../useJournalEntriesTableFilter';

export const STORYBOOK_JOURNAL_ENTRIES_LISTING_FILTER_QUERY_KEY =
  'journal-entries-listing-filter';
export const STORYBOOK_JOURNAL_ENTRIES_LISTING_FILTER_VALUE_QUERY_KEY =
  'journal-entries-listing-filter-value';

type Props = {
  hide?: (
    | 'classification'
    | 'book'
    | 'type'
    | 'status'
    | 'party'
    | 'subType'
    | 'account'
    | 'listing'
    | 'date'
    | 'amount'
    | 'bookingChannel'
  )[];
  renderExcludedStatus?: (content: ReactNode) => ReactNode;
};

export const JournalEntriesTableFilter = ({
  hide,
  renderExcludedStatus,
  view,
}: Props & ComponentProps<typeof Account>) => {
  'use memo';

  return (
    <Group gap="xs">
      <Search />
      {!hide?.includes('book') && <Book />}
      {!hide?.includes('status') && (
        <>
          {renderExcludedStatus?.(<ExcludedStatus />)}
          <PublishedStatus />
        </>
      )}
      {!hide?.includes('date') && <Date />}
      {!hide?.includes('amount') && <AmountFilter />}
      {!hide?.includes('listing') && <Listing />}
      {!hide?.includes('account') && <Account view={view} />}
      {!hide?.includes('classification') && <AccountClassificationFilter />}
      {!hide?.includes('party') && <Party />}
      {!hide?.includes('type') && <Type />}
      {!hide?.includes('subType') && <SubType view={view} />}
      {!hide?.includes('bookingChannel') && <BookingChannel />}
    </Group>
  );
};

const Book = () => {
  'use memo';

  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <CustomFilter.Book
      book={filter.ledger}
      setBook={(ledger) => setFilter({ ledger })}
    />
  );
};

const Search = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(v) => setFilter({ search: v || null })}
    />
  );
};

const AmountFilter = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <Filter.Amount
      value={filter.amount}
      setValue={(amount) => setFilter({ amount: amount?.text || null })}
    />
  );
};

const ExcludedStatus = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  const value =
    (filter.status ?? 'all') === 'all'
      ? null
      : (filter.status as activeStatus_enum | null);

  return (
    <Filter.Status
      value={value}
      label="Exclusion"
      setValue={(v) => {
        setFilter({ status: v ?? 'all' });
      }}
      activeLabel="Included"
      inactiveLabel="Excluded"
      clearable
    />
  );
};

const PublishedStatus = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  const options = useMemo<
    SelectItem<NonNullable<(typeof filter)['published']>>[]
  >(
    () => [
      {
        label: 'Draft',
        value: 'draft',
        description:
          'Entries that have not been yet attached to an owner statement.',
        icon: (
          <Box
            w={10}
            h={10}
            sx={(theme) => ({
              alignSelf: 'center',
              borderRadius: theme.radius.xl,
              backgroundColor: theme.colors.yellow[6],
            })}
          />
        ),
      },
      {
        label: 'Published',
        value: 'published',
        description:
          'Entries have been locked & attached to an owner statement.',
        icon: (
          <Box
            w={10}
            h={10}
            sx={(theme) => ({
              alignSelf: 'center',
              borderRadius: theme.radius.xl,
              backgroundColor: theme.colors.green[6],
            })}
          />
        ),
      },
    ],
    []
  );

  const value = options.find((i) => i.value === filter.published) || null;

  return (
    <Filter.Select
      value={value}
      label="Status"
      setValue={(v) =>
        setFilter({
          published: v?.value || null,
        })
      }
      type="single"
      data={{
        options,
      }}
      hideIcon
      popoverWidth={300}
    />
  );
};

const Type = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <JournalEntryTypeFilter
      value={filter.type}
      setValue={(v) => setFilter({ type: v || null })}
    />
  );
};

const SubType = ({
  view,
}: {
  view: ComponentProps<typeof JournalEntrySubTypeFilter>['view'];
}) => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <JournalEntrySubTypeFilter
      value={filter.subType}
      setValue={(v) => setFilter({ subType: v || null })}
      view={view}
    />
  );
};

const Party = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <CustomFilter.Party
      party={filter.party}
      setParty={(v) => setFilter({ party: v })}
    />
  );
};

const Date = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <Filter.Date
      value={filter.date || undefined}
      setValue={(value) => {
        setFilter({
          date:
            value
              ?.filter(hasValue)
              .map((date) => day(date).yyyymmdd())
              .join('...') || null,
        });
      }}
    />
  );
};

const Listing = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <CustomFilter.Listings
      listingIds={filter.listingIds}
      setListingIds={(v) => setFilter({ listingIds: v || null })}
      onUnmappedSelected={(v) => setFilter({ listingIds: [v] })}
    />
  );
};

const Account = ({
  view,
}: {
  view: ComponentProps<typeof JournalEntryAccountFilter>['view'];
}) => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <JournalEntryAccountFilter
      value={filter.accountIds}
      setValue={(v) => setFilter({ accountIds: v || null })}
      view={view}
    />
  );
};

const AccountClassificationFilter = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <CustomFilter.Classification
      type="multiple"
      classification={filter.classifications?.filter(hasValue)}
      setClassification={(classifications) => setFilter({ classifications })}
    />
  );
};

const BookingChannel = () => {
  const { filter, setFilter } = useJournalEntriesTableFilter();

  return (
    <CustomFilter.BookingChannel
      bookingChannels={filter.bookingChannels}
      setBookingChannels={(v) =>
        setFilter({ bookingChannels: v?.length ? v : null })
      }
    />
  );
};
